/** @jsx jsx */
import { Fragment, useEffect, useRef, useState } from 'react'
import { Box, Button, Flex, Heading, Spinner, Text, jsx } from 'theme-ui'
import { useCart, useTranslate } from '@chordcommerce/gatsby-theme-autonomy'
import FormInputWithLabel from '~/components/Generic/Form/InputWithLabel'
import { useCanCount } from '~/hooks/components/use-can-count'
import { useCartFlyout } from '~/hooks/components/use-cart-flyout'
import ChevronIcon from '~/assets/images/icons/chevron.inline.svg'
import GlassIcon from '~/assets/images/icons/glass.inline.svg'

const GetWineGiveWineTrigger = ({
  address,
  isCartFlyoutOpen,
  isHidden,
  isOverlay,
  onClick,
}) => {
  const translate = useTranslate()

  if (address) {
    return (
      <Button
        variant="reset"
        onClick={onClick}
        py={2}
        px={3}
        sx={{
          boxShadow:
            isOverlay &&
            'inset 0px 8px 8px -6px rgba(0,0,0,.1), inset 0px -8px 8px -6px rgba(0,0,0,.1)',
          display: !isHidden ? 'block' : 'none',
          width: '100%',
        }}
      >
        <Flex mb={1} sx={{ justifyContent: 'space-between' }}>
          <Text as="strong" variant="body3" sx={{ fontWeight: 'bold' }}>
            {`${translate('cart.gwgw.added')}:`}
          </Text>
          <Text
            as="span"
            variant="body3"
            sx={{ color: 'brandPrimary', textDecoration: 'underline' }}
          >
            {translate('cart.gwgw.edit')}
          </Text>
        </Flex>
        <Text
          as="p"
          m={0}
          variant="description"
          color="greyDark"
          sx={{
            overflow: 'hidden',
            textAlign: 'left',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {address}
        </Text>
      </Button>
    )
  }

  return (
    <Button
      variant="reset"
      onClick={onClick}
      pt={2}
      pb="20px"
      sx={{
        alignItems: 'center',
        boxShadow:
          isOverlay &&
          'inset 0px 8px 8px -6px rgba(0,0,0,.1), inset 0px -8px 8px -6px rgba(0,0,0,.1)',
        display: !isHidden ? 'block' : 'none',
        textAlign: 'center',
        width: '100%',
      }}
    >
      <Flex
        sx={{
          alignItems: 'center',
          color: 'brandPrimary',
          justifyContent: 'center',
        }}
      >
        <GlassIcon
          sx={{
            height: '28px',
            mr: 2,
            opacity: !isCartFlyoutOpen && isOverlay ? 0 : 1,
            pointerEvents: 'none',
            position: 'relative',
            transform:
              !isCartFlyoutOpen && isOverlay
                ? 'translateX(-75px) rotate(0deg)'
                : 'translateX(0) rotate(5deg)',
            transition: 'all .2s .2s ease-in-out',
            top: '4px',
            width: 'auto',
          }}
        />
        <Text as="span" variant="textLink" sx={{ textDecoration: 'underline' }}>
          {translate('cart.gwgw.toggle')}
        </Text>
        <GlassIcon
          sx={{
            height: '28px',
            ml: 2,
            opacity: !isCartFlyoutOpen && isOverlay ? 0 : 1,
            pointerEvents: 'none',
            position: 'relative',
            transform:
              !isCartFlyoutOpen && isOverlay
                ? 'translateX(75px) rotate(0deg)'
                : 'translateX(0) rotate(-5deg)',
            transition: 'all .2s .2s ease-in-out',
            top: '4px',
            width: 'auto',
          }}
        />
      </Flex>
    </Button>
  )
}

const GetWineGiveWineInline = ({
  address,
  error,
  isLoading,
  isOpen,
  onChange,
  onClose,
  onSave,
}) => {
  const translate = useTranslate()

  return (
    <Box py={2} sx={{ display: isOpen ? 'block' : 'none' }}>
      <GetWineGiveWineForm
        address={address}
        buttonVariant="secondary"
        error={error}
        isLoading={isLoading}
        onChange={onChange}
        onSave={onSave}
      />
      <Box mt={2}>
        <Button
          onClick={onClose}
          variant="reset"
          sx={{
            alignItems: 'center',
            display: 'flex',
            height: '32px',
            width: 'calc(100% + 32px)',
          }}
        >
          <Text
            as="p"
            m={0}
            variant="body3"
            sx={{ color: 'brandPrimary', textDecoration: 'underline' }}
          >
            {translate('cart.gwgw.close')}
          </Text>
        </Button>
      </Box>
    </Box>
  )
}

const GetWineGiveWineOverlay = ({
  address,
  error,
  isLoading,
  isOpen,
  onChange,
  onClose,
  onSave,
}) => {
  const translate = useTranslate()

  return (
    <Box
      bg="backgroundLight"
      px={[2, 3]}
      pb={[2, 3]}
      sx={{
        bottom: '0',
        boxShadow: '0px 5px 15px 0px rgba(0, 0, 0, 0.2)',
        left: '0',
        overflowY: 'scroll',
        position: 'absolute',
        right: '0',
        top: '0',
        transform: isOpen ? 'translateX(0)' : 'translateX(500px)',
        transition: 'transform 0.2s ease-in-out',
        zIndex: '201',
      }}
    >
      <Box
        mb={3}
        sx={{
          borderBottom: '1px solid',
          borderBottomColor: 'greyLight',
        }}
      >
        <Button
          onClick={onClose}
          variant="reset"
          ml={-2}
          pl={2}
          sx={{
            alignItems: 'center',
            display: 'flex',
            height: '64px',
            width: 'calc(100% + 32px)',
          }}
        >
          <ChevronIcon
            sx={{
              height: '10px',
              pointerEvents: 'none',
              position: 'relative',
              top: '1px',
              transform: 'rotate(90deg)',
              width: '16px',
            }}
          />
          <Text
            as="p"
            m={0}
            ml={1}
            variant="title5"
            sx={{ position: 'relative', top: '-1px' }}
          >
            {translate('cart.gwgw.back_cart')}
          </Text>
        </Button>
      </Box>
      <GetWineGiveWineForm
        address={address}
        buttonVariant="primary"
        error={error}
        isLoading={isLoading}
        onChange={onChange}
        onSave={onSave}
      />
    </Box>
  )
}

const GetWineGiveWineForm = ({
  address,
  buttonVariant,
  error,
  isLoading,
  onChange,
  onSave,
}) => {
  const buttonRef = useRef(null)
  const translate = useTranslate()

  return (
    <Box>
      <Box mb={3}>
        <Heading
          as="label"
          variant="body2"
          mb={1}
          htmlFor="gwgw-email"
          sx={{ display: 'block' }}
        >
          {translate('cart.gwgw.form_heading')}
        </Heading>
        <Text as="p" variant="body3">
          {translate('cart.gwgw.form_description')}
        </Text>
      </Box>
      <Box mb={2}>
        <FormInputWithLabel
          name="gwgw-email"
          title={'Email address'}
          label={'Email address'}
          aria-label={'Email address'}
          placeholder={'Email address'}
          defaultValue={address}
          type="email"
          onClick={(e) =>
            buttonRef.current.scrollIntoView({
              block: 'center',
              behavior: 'smooth',
            })
          }
          onChange={onChange}
        />
      </Box>
      {error && (
        <Text as="p" mb={2} variant="error">
          {error}
        </Text>
      )}
      <Button
        onClick={onSave}
        variant={buttonVariant}
        ref={buttonRef}
        type="button"
        sx={{ textAlign: 'center', width: '100%' }}
      >
        {isLoading && (
          <Spinner
            size="15"
            color="inherit"
            sx={{
              '@media (prefers-reduced-motion)': {
                circle: {
                  animationIterationCount: 8,
                },
              },
            }}
          />
        )}
        {!isLoading && translate('cart.gwgw.save')}
      </Button>
    </Box>
  )
}

const GetWineGiveWine = ({ isOverlay }) => {
  const { cart, modifyCart } = useCart()
  const { isCartFlyoutOpen } = useCartFlyout()
  const translate = useTranslate()
  const address = cart?.metadata?.mwcGwgwAddress

  const [updatedAddress, setUpdatedAddress] = useState(address)
  const [apiError, setApiError] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)

  const updateAddress = async (addressToUpdate) =>
    modifyCart({
      attributes: { metadata: { mwcGwgwAddress: addressToUpdate } },
    })

  const handleUpdateAddress = async (e) => {
    if (isLoading) return

    try {
      setIsLoading(true)
      await updateAddress(updatedAddress)
      setIsExpanded(false)
      setIsLoading(false)
    } catch (error) {
      setApiError(translate('error.api.default'))
      console.error(error, { source: 'GetWineGiveWine.handleUpdateAddress' })
    }
  }

  useEffect(() => {
    // Close the flyout overlay when it's clicked outside.
    if (isOverlay && !isCartFlyoutOpen) {
      setIsExpanded(false)
    }
  }, [isCartFlyoutOpen, isOverlay])

  const GetWineGiveWineComponent = isOverlay
    ? GetWineGiveWineOverlay
    : GetWineGiveWineInline

  return (
    <Box bg="backgroundHolidayBlue">
      <GetWineGiveWineTrigger
        address={address}
        isCartFlyoutOpen={isCartFlyoutOpen}
        isHidden={isExpanded && !isOverlay}
        isOverlay={isOverlay}
        onClick={() => setIsExpanded(true)}
      />
      <Box
        sx={{
          // Hides the form from tabbing unless open. Delays re-hiding on close
          // until overlay animation is complete.
          visibility: 'visible',
          animation: !isExpanded && '0s linear .2s forwards delayedHide',
          '@keyframes delayedHide': {
            to: {
              visibility: 'hidden',
            },
          },
        }}
      >
        <GetWineGiveWineComponent
          address={updatedAddress}
          error={apiError}
          isLoading={isLoading}
          isOpen={isExpanded}
          onChange={(e) => setUpdatedAddress(e.target.value)}
          onClose={() => setIsExpanded(false)}
          onSave={handleUpdateAddress}
        />
      </Box>
    </Box>
  )
}

const GetWineGiveWineGate = ({ isOverlay = true }) => {
  const canCount = useCanCount()
  const getWineGiveWineCount = process.env.GATSBY_GWGW_COUNT

  if (!getWineGiveWineCount || canCount < getWineGiveWineCount) {
    return <Fragment />
  }

  return <GetWineGiveWine isOverlay={isOverlay} />
}

export default GetWineGiveWineGate
